import { IconButton, Snackbar } from '@mui/material';
import { Alert } from '@mui/material';
import { Button as AntdButton, Input, Modal, Table, Tooltip } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { handleUpdateCustomAssignment } from 'store/activities/thunks';
import { useAllDistrictTeacher, useCurrentUser } from 'store/users/selectors';
import styled from 'styled-components';
import color from 'utils/constants/color';
import { useShareNotification } from 'utils/methods/firebaseShareAssignment';
import { filter } from 'lodash';
import moment from 'moment-timezone';
import useBookAvailabilityCheck from 'components/hooks/books/useBookAvailabilityCheck';
import shareIconSvg from "../utils/assets/images/shareIcon.svg";
import SentryWrap from 'components/Sentry';

const CustomTable = styled(Table)`
  .ant-table-thead > tr > th {
    background-color: rgb(239, 246, 254);
  }
  .ant-table-tbody > tr > td {
    background-color: #fff;
  }
  .ant-table-header table,
  .ant-table-body table {
    table-layout: fixed;
    width: 100% !important;
  }
`;

const TableContainer = styled.div`
  margin-bottom: 20px;
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 5px;
  background: #fff;
  border-radius: 20px;
  position: relative;
  border: 1px solid;
  margin-top: 2px;
  margin-bottom: 10px;
  max-width: '250px';
  @media (max-width: 600px){
    margin-right: 15px;
  }
`;

const StyledInput = styled(Input)`
  border: none;
  outline: none;
  background: transparent !important;
  &:focus {
    box-shadow: none;
  }
  flex-grow: 1;
`;

const AssignableNotificationShare = ({ assignment, exameshare = true }) => {
  const dispatch = useDispatch();
  const currentUser = useCurrentUser();
  const allDistrictTeacher = useAllDistrictTeacher();
  const { createShareNotification } = useShareNotification();
  const [shareLoader, setShareLoader] = useState(false);
  const [shareModal, setShareModal] = useState(false);
  const [tablePagination, setTablePagination] = useState({ current: 1, pageSize: 8 });
  const filterShareBy = useMemo(
    () => filter(
      allDistrictTeacher,
      (item) => (assignment?.modifiedOn ? true : item?.id !== assignment?.share_by),
    ),
    [allDistrictTeacher, assignment?.modifiedOn, assignment?.share_by],
  );
  const [searchText,setSearchText] = useState('');
  const [alert, setAlert] = useState({
    open: false,
    message: '',
  });

  const assignmentBookId = assignment?.chapter || assignment?.bookId;
  const checkBookAvailability = useBookAvailabilityCheck(assignmentBookId, 'Activity');

  useEffect(() => {
    if (shareModal) {
      document.body.style.overflowX = 'hidden';
    } else {
      document.body.style.overflowX = 'auto';
    }
  }, [shareModal]);

  useEffect(() => {
    if (shareModal) {
      document.body.classList.add("body-overflow-hidden");
    }
    return () => {
      document.body.classList.remove("body-overflow-hidden");
    };
  }, [shareModal]);

  const AssignmentShareList =
    assignment?.share_list?.length > 0 ? assignment?.share_list : [];

  const hideShareModal = () => {
    setShareModal(false);
    setTablePagination({ current: 1, pageSize: 8 });
    setSearchText('');
  };

  const ViewShareModal = () => {
    setShareModal(true);
  };

  const handleSend = async (selectedUsers) => {
    if (!checkBookAvailability()) {
      return;
    }
    
    setShareLoader(selectedUsers);
    const selectedTeacher = [selectedUsers];
    const sharedList =
      assignment?.share_list?.length > 0 ? assignment?.share_list : [];
    const data = {
      ...assignment,
      share_list: [...selectedTeacher, ...sharedList],
      share_by: assignment?.createdBy,
      shared_time: moment().toISOString()
    };
    const updateData = await dispatch(handleUpdateCustomAssignment(data));
    if (updateData) {
      const {
        bookDescription,
        category,
        colorCode,
        level,
        linked_to,
        questions,
        ...remainingData
      } = updateData;
      const notificationData = {
        ...remainingData,
        notification_list: [...selectedTeacher],
        message: `${currentUser?.firstName} ${currentUser?.lastName} Share '${updateData?.displayName}' Activity`,
        checkBookExist: true,
        resourceType: window.location.href?.includes("video") ? true : false,
      };
      createShareNotification(notificationData, assignment?.createdBy);
    }
    setShareLoader(false);
    setAlert({
      open: true,
      message: 'Activity shared successfully',
    });
  };

  const handleCloseAlert = () => {
    setAlert({
      open: false,
      message: '',
    });
  };

  const filterSearchData = useMemo(() => {
    const normalize = (value) =>
      String(value ?? '')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '');

    const query = normalize(searchText);
    if (!query) return filterShareBy;

    return filterShareBy?.filter((obj) => {
      const firstName = normalize(obj?.firstName);
      const lastName = normalize(obj?.lastName);
      const fullName = `${firstName}${lastName}`;
      const reverseFullName = `${lastName}${firstName}`;

      return (
        firstName.includes(query) ||
        lastName.includes(query) ||
        fullName.includes(query) ||
        reverseFullName.includes(query)
      );
    });
  }, [searchText, filterShareBy]);

  const handleInputChange = (text) => {
    setTablePagination((prev) => ({ ...prev, current: 1 }));
    setSearchText(text);
  };

  const handlePaginationChange = (page, size) => {
    setTablePagination((prev) => ({
      current: page,
      pageSize:
        size !== undefined && size !== null ? Number(size) : prev.pageSize,
    }));
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      ellipsis: true,
      width: 148,
      render: (render) => `${render.firstName} ${render.lastName}`,
    },
    {
      title: 'Status',
      key: 'status',
      width: 148,
      align: 'left',
      render: (render) => (
        <div
          style={{ cursor: 'pointer' }}
          onClick={() => {
            if (!AssignmentShareList?.includes(render?.id)) {
              handleSend(render?.id);
            }
          }}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            <AntdButton
              type='primary'
              loading={shareLoader === render?.id}
              style={{
                borderRadius: 10,
                minWidth: 108,
                backgroundColor: AssignmentShareList?.includes(render?.id)
                  ? color.greentrue
                  : color.green100,
                border: '0px',
              }}
            >
              {AssignmentShareList?.includes(render?.id) ? 'Shared' : 'Share'}
            </AntdButton>
          </span>
        </div>
      ),
    },
  ];

  return (
    <SentryWrap>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={alert.open}
        autoHideDuration={3000}
        onClose={handleCloseAlert}
      >
        <Alert onClose={handleCloseAlert} severity='success'>
          <strong>ALERT:</strong> {alert.message}
        </Alert>
      </Snackbar>
      <Modal
        open={shareModal}
        onCancel={hideShareModal}
        className='activeStudentModal'
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <InputContainer>
            <StyledInput
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder='Search District Teacher'
              maxLength={60}
              autoFocus
              value={searchText}
            />
          </InputContainer>
        </div>
        <div>
          <div className='activeStudentMdlContent'>
            <TableContainer>
              <CustomTable
                rowKey='id'
                columns={columns}
                dataSource={filterSearchData}
                scroll={{ x: 568, y: 320 }}
                sticky
                pagination={{
                  position: 'bottomRight',
                  current: tablePagination.current,
                  pageSize: tablePagination.pageSize,
                  pageSizeOptions: ['8', '10', '20', '50', '100'],
                  onChange: handlePaginationChange,
                  showSizeChanger: {
                    getPopupContainer: () => document.body,
                    showSearch: false,
                  },
                }}
              />
            </TableContainer>
          </div>
        </div>
      </Modal>
      <IconButton
        color='primary'
        style={{
          padding: 0,
        }}
        className={`preview-edit-texts ${
          exameshare ? 'preview-edit-text' : 'previewEditTxt'
        }`}
        onClick={() => {
          ViewShareModal();
        }}
      >
          <img src={shareIconSvg} alt="" title="" />
      </IconButton>
    </SentryWrap>
  );
};

export default AssignableNotificationShare;